import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { AdminPageWrapper } from "../AdminPageWrapper";
import {
  HStack,
  Stack,
  Text,
  Table,
  Spinner,
  Checkbox,
  Icon,
  ButtonGroup,
  IconButton,
} from "@chakra-ui/react";
import { AdminView } from "../../models/AdminView";
import { toast } from "../../../shared/views/CustomToaster";
import { PageControls } from "../../../pages/map/views/BottomBar/cra-way/PageControls";
import { AdminSurveyApi } from "../../services/AdminSurveyApi";
import { AdminSurvey } from "../../../survey/models/Survey";
import { Page } from "../../../shared/utils/http-utils";
import { Link } from "react-router-dom";
import { appRoutes } from "../../../../App";
import { AddIcon, ExternalLinkIcon } from "../../../shared/views/icons";
import { SurveyApi } from "../../../survey/services/SurveyApi";

const pageSize = 20;

const AdminSurveysPageBase: React.FC = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState<Page<AdminSurvey>>();

  const goToPage = async (pageNumber: number) => {
    setLoading(true);
    const surveysPage = await AdminSurveyApi.page(pageSize, pageNumber).then(
      (res) => res.data,
    );
    setPage(surveysPage);
    setLoading(false);
  };

  useEffect(() => {
    goToPage(pageIndex);
  }, [pageIndex]);

  const setExcludeFromAnalysis = async (
    surveyId: string,
    excludeFromAnalysis: boolean,
  ) => {
    await AdminSurveyApi.put(surveyId, { excludeFromAnalysis })
      .then((res) => {
        const updatedSurvey = res.data;
        const updatedData = page!.data.map((p) =>
          p.id === updatedSurvey.id ? updatedSurvey : p,
        );
        setPage({ ...page!, data: updatedData });
        toast("success", "Survey updated");
      })
      .catch((err) => {
        console.error(err);
        toast("error", "Failed to update survey");
      });
  };

  const addSurveyToLibrary = async (surveyId: string) => {
    await SurveyApi.postAccountAccess(surveyId)
      .then(() => {
        toast("success", "Survey added");
        goToPage(pageIndex);
      })
      .catch(() => {
        toast("error", "Error. Probably because it's already in your library.");
      });
  };

  return (
    <AdminPageWrapper selectedView={AdminView.Surveys}>
      <Stack px={6}>
        <Text fontSize="2xl">{AdminView.Surveys}</Text>

        <HStack justifyContent={"end"}>
          <PageControls
            pageSize={pageSize}
            pageIndex={pageIndex}
            setPageIndex={setPageIndex}
            nItems={page?.meta?.totalElements ?? 0}
          />
        </HStack>

        {loading ? (
          <Spinner color={"primary.500"} />
        ) : (
          <Table.Root striped>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Name</Table.ColumnHeader>
                <Table.ColumnHeader>City</Table.ColumnHeader>
                <Table.ColumnHeader>Exclude from analysis</Table.ColumnHeader>
                <Table.ColumnHeader textAlign={"right"}>
                  Actions
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {page?.data.map((survey) => (
                <Table.Row key={survey.id}>
                  <Table.Cell>
                    <Link to={appRoutes.survey(survey.id)}>
                      <HStack _hover={{ textDecor: "underline" }}>
                        <Text>{survey.name}</Text>
                        <Icon as={ExternalLinkIcon} />
                      </HStack>
                    </Link>
                  </Table.Cell>
                  <Table.Cell>{survey.city.name}</Table.Cell>
                  <Table.Cell>
                    <Checkbox.Root
                      size="sm"
                      colorPalette="primary"
                      checked={survey.excludeFromAnalysis}
                      onCheckedChange={({ checked }) => {
                        setExcludeFromAnalysis(survey.id, !!checked);
                      }}
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                    </Checkbox.Root>
                  </Table.Cell>
                  <Table.Cell textAlign={"right"} py={0}>
                    <ButtonGroup size="sm">
                      <IconButton
                        title="Add to my library"
                        variant="outline"
                        colorPalette={"primary"}
                        onClick={() => addSurveyToLibrary(survey.id)}
                      >
                        <AddIcon />
                      </IconButton>
                    </ButtonGroup>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Stack>
    </AdminPageWrapper>
  );
};

export const AdminSurveysPage = observer(AdminSurveysPageBase);
